import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { formatTimeAgo, formatNumber, getInitials, cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Heart, MessageCircle, Share2, MoreHorizontal, Trash2, Flag, Crown, Play, Volume2, VolumeX, Maximize, Minimize, X, Eye, BarChart3, Send, Search, Users, Gift } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { HashtagText } from './Hashtags';
import { VerifiedBadge } from '../ui/verified-badge';
import SendGiftModal from '../gifts/SendGiftModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PostCard = ({ post, onUpdate, onDelete, className, style }) => {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  const [liking, setLiking] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const videoRef = useRef(null);
  const fullscreenVideoRef = useRef(null);
  const cardRef = useRef(null);
  const viewTrackedRef = useRef(false);
  const viewTimerRef = useRef(null);
  const [localViewsCount, setLocalViewsCount] = useState(post.views_count || 0);

  // Fire the view-count endpoint when the post has been on-screen
  // for >= 1.5s. Backend dedupes per (post, user) — we also keep a
  // session-local guard via viewTrackedRef.
  const trackView = useCallback(async () => {
    if (viewTrackedRef.current) return;
    if (!post?.id || !token) return;
    if (user?.id && post.user_id === user.id) return; // self-view ignored
    viewTrackedRef.current = true;
    try {
      const r = await axios.post(`${API}/posts/${post.id}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.counted) {
        setLocalViewsCount((c) => Math.max(c, r.data.views_count || (c + 1)));
      }
    } catch {
      viewTrackedRef.current = false; // allow retry next time
    }
  }, [post?.id, post?.user_id, token, user?.id]);
  
  // Get proper media URL (handle local vs cloudinary)
  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  // Intersection Observer for lazy loading and video auto-play
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);

        // View-counting: 1.5s dwell time while visible >= 50%
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!viewTimerRef.current && !viewTrackedRef.current) {
            viewTimerRef.current = setTimeout(() => {
              viewTimerRef.current = null;
              trackView();
            }, 1500);
          }
        } else if (viewTimerRef.current) {
          clearTimeout(viewTimerRef.current);
          viewTimerRef.current = null;
        }

        // Auto-play video when visible
        if (entry.isIntersecting && videoRef.current && post.media_type === 'video') {
          videoRef.current.play().catch(() => {});
          setIsPlaying(true);
          // Video play also counts as a view immediately (no dwell needed).
          trackView();
        } else if (!entry.isIntersecting && videoRef.current) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      },
      { threshold: [0, 0.5, 0.9] }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      observer.disconnect();
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
        viewTimerRef.current = null;
      }
    };
  }, [post.media_type, trackView]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.muted = !isMuted;
    }
  };

  // Open fullscreen video player
  const openFullscreen = useCallback((e) => {
    e.stopPropagation();
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
    // Pause inline video
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  // Close fullscreen video player
  const closeFullscreen = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsFullscreen(false);
    document.body.style.overflow = '';
    // Resume inline video if visible
    if (videoRef.current && isVisible) {
      videoRef.current.play().catch(() => {});
    }
  }, [isVisible]);

  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        closeFullscreen();
      }
    };
    if (isFullscreen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, closeFullscreen]);

  // Sync fullscreen video time with inline video
  useEffect(() => {
    if (isFullscreen && fullscreenVideoRef.current && videoRef.current) {
      fullscreenVideoRef.current.currentTime = videoRef.current.currentTime;
      fullscreenVideoRef.current.muted = isMuted;
      fullscreenVideoRef.current.play().catch(() => {});
    }
  }, [isFullscreen, isMuted]);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (liking) return;
    
    setLiking(true);
    try {
      if (post.is_liked) {
        await axios.delete(`${API}/posts/${post.id}/like`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        onUpdate(post.id, { is_liked: false, likes_count: post.likes_count - 1 });
      } else {
        await axios.post(`${API}/posts/${post.id}/like`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        onUpdate(post.id, { is_liked: true, likes_count: post.likes_count + 1 });
      }
    } catch (error) {
      console.error('Failed to like post:', error);
    } finally {
      setLiking(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/posts/${post.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onDelete(post.id);
      toast.success('Deleted');
    } catch (error) {
      toast.error('Error');
    }
  };

  // Share externally (system share or clipboard)
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'FityH',
          text: post.content,
          url: `${window.location.origin}/post/${post.id}`
        });
      } catch (error) {
        console.error('Share failed:', error);
      }
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
      toast.success('Link copied!');
    }
  };

  // Share with a user in app (opens chat selection)
  const [showShareModal, setShowShareModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [shareSearchQuery, setShareSearchQuery] = useState('');
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [sendingShare, setSendingShare] = useState(false);

  const searchUsersForShare = async (query) => {
    if (!query || query.length < 2) {
      setShareSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out current user
      setShareSearchResults((response.data || []).filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleShareToUser = async (targetUser) => {
    setSendingShare(true);
    try {
      // Create a shared post message
      const shareMessage = {
        type: 'shared_post',
        post_id: post.id,
        content: `📤 Pataje yon post ak ou!`,
        post_preview: {
          id: post.id,
          content: post.content?.substring(0, 100) + (post.content?.length > 100 ? '...' : ''),
          media: post.media?.[0],
          media_type: post.media_type,
          username: post.username,
          user_avatar: post.user_avatar
        }
      };
      
      await axios.post(`${API}/chat/send`, {
        recipient_id: targetUser.id,
        content: shareMessage.content,
        message_type: 'shared_post',
        metadata: shareMessage.post_preview
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Pataje ak @${targetUser.username}!`);
      setShowShareModal(false);
      setShareSearchQuery('');
      setShareSearchResults([]);
    } catch (error) {
      toast.error('Erè pandan pataj');
    } finally {
      setSendingShare(false);
    }
  };

  const goToPost = () => {
    navigate(`/post/${post.id}`);
  };

  const goToComments = (e) => {
    e.stopPropagation();
    navigate(`/post/${post.id}`, { state: { focusComment: true } });
  };

  const isOwner = user?.id === post.user_id;
  const isCreator = post.is_creator || post.followers_count > 1000;
  // Verification is set by backend when user has 50K+ followers
  const isVerified = post.is_verified;

  return (
    <article 
      ref={cardRef}
      className={cn(
        "p-4 cursor-pointer transition-all duration-200",
        "hover:bg-gradient-to-r hover:from-pink-500/5 hover:to-violet-500/5",
        "border-b border-border/30",
        className
      )}
      style={style}
      onClick={goToPost}
      data-testid={`post-card-${post.id}`}
    >
      <div className="flex gap-3">
        {/* Avatar with ring */}
        <Link 
          to={`/profile/${post.username}`}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0"
        >
          <div className="relative">
            <Avatar className={cn(
              "w-12 h-12 ring-2 transition-all duration-300",
              isCreator ? "ring-pink-500/50 hover:ring-pink-500" : "ring-border/50 hover:ring-primary/50"
            )}>
              <AvatarImage src={post.avatar} alt={post.display_name} />
              <AvatarFallback className="bg-gradient-to-br from-pink-500/20 to-violet-500/20 text-primary font-semibold">
                {getInitials(post.display_name || post.username)}
              </AvatarFallback>
            </Avatar>
            {/* Creator Badge on Avatar */}
            {isCreator && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 flex items-center justify-center ring-2 ring-background">
                <Crown className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
        </Link>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <Link 
                to={`/profile/${post.username}`}
                onClick={(e) => e.stopPropagation()}
                className="font-bold hover:underline truncate text-foreground"
              >
                {post.display_name || post.username}
              </Link>
              
              {/* Verified Badge - 50K+ followers */}
              {isVerified && (
                <VerifiedBadge 
                  isVerified={true} 
                  verificationType={post.verification_type || 'verified'}
                  size="sm"
                />
              )}
              
              {/* Creator Badge - 1K+ followers (only if not verified) */}
              {isCreator && !isVerified && (
                <Crown className="w-4 h-4 text-amber-500 shrink-0" />
              )}
              
              <Link 
                to={`/profile/${post.username}`}
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground text-sm truncate"
              >
                @{post.username}
              </Link>
              <span className="text-muted-foreground/50">·</span>
              <span className="text-muted-foreground text-sm whitespace-nowrap">
                {formatTimeAgo(post.created_at, language)}
              </span>
            </div>
            
            {/* More Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 rounded-full hover:bg-secondary transition-all"
                  data-testid={`post-menu-${post.id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                {isOwner ? (
                  <DropdownMenuItem 
                    onClick={handleDelete} 
                    className="text-destructive rounded-lg"
                    data-testid={`delete-post-${post.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem className="rounded-lg">
                    <Flag className="w-4 h-4 mr-2" />
                    Report
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Post Content */}
          <div className="mt-2">
            <HashtagText content={post.content} className="break-words text-[15px] leading-relaxed" />
          </div>
          
          {/* Media */}
          {post.media && post.media.length > 0 && (
            <div className="mt-3 rounded-2xl overflow-hidden bg-secondary/30 relative group">
              {post.media_type === 'image' && !imageError && (
                <img 
                  src={getMediaUrl(post.media[0])} 
                  alt="Post media"
                  className={cn(
                    "w-full max-h-[500px] object-cover transition-all duration-500",
                    isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                  )}
                  loading="lazy"
                  onError={() => setImageError(true)}
                />
              )}
              {post.media_type === 'image' && imageError && (
                <div className="w-full h-48 flex flex-col items-center justify-center bg-secondary/50 text-muted-foreground">
                  <X className="w-10 h-10 mb-2 text-red-400" />
                  <p className="text-sm">Imaj pa disponib</p>
                </div>
              )}
              {post.media_type === 'video' && !videoError && (
                <div className="relative bg-black aspect-[9/16] sm:aspect-video max-h-[80vh]">
                  <video 
                    ref={videoRef}
                    src={getMediaUrl(post.media[0])} 
                    className="w-full h-full object-cover cursor-pointer"
                    muted={isMuted}
                    loop
                    playsInline
                    webkit-playsinline="true"
                    preload="auto"
                    onClick={openFullscreen}
                    onLoadedData={() => console.log('Video loaded:', post.id)}
                    onError={() => setVideoError(true)}
                    data-testid={`video-player-${post.id}`}
                  />
                  
                  {/* Video Controls Overlay */}
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    {/* Fullscreen Button */}
                    <button
                      onClick={openFullscreen}
                      className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-all"
                      data-testid={`fullscreen-btn-${post.id}`}
                    >
                      <Maximize className="w-5 h-5" />
                    </button>
                    {/* Mute/Unmute Button */}
                    <button
                      onClick={toggleMute}
                      className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-all"
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {/* Play indicator when paused */}
                  {!isPlaying && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer"
                      onClick={openFullscreen}
                    >
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-8 h-8 text-black ml-1" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {post.media_type === 'video' && videoError && (
                <div className="w-full aspect-video flex flex-col items-center justify-center bg-secondary/50 text-muted-foreground">
                  <X className="w-12 h-12 mb-2 text-red-400" />
                  <p className="text-sm font-medium">Videyo pa disponib</p>
                  <p className="text-xs mt-1 text-muted-foreground/60">Fichye a te efase oswa lyen an ekspire</p>
                </div>
              )}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-1 mt-3 -ml-2">
            {/* Like Button */}
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "gap-1.5 rounded-full transition-all duration-200 h-9 px-3",
                "hover:bg-pink-500/10 hover:text-pink-500",
                post.is_liked && "text-pink-500"
              )}
              onClick={handleLike}
              data-testid={`like-btn-${post.id}`}
            >
              <Heart className={cn(
                "w-[18px] h-[18px] transition-transform duration-200",
                post.is_liked && "fill-current scale-110",
                liking && "animate-pulse"
              )} />
              <span className="text-sm font-medium">{formatNumber(post.likes_count)}</span>
            </Button>
            
            {/* Comment Button */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 rounded-full hover:bg-blue-500/10 hover:text-blue-500 transition-all duration-200 h-9 px-3"
              onClick={goToComments}
              data-testid={`comment-btn-${post.id}`}
            >
              <MessageCircle className="w-[18px] h-[18px]" />
              <span className="text-sm font-medium">{formatNumber(post.comments_count)}</span>
            </Button>
            
            {/* Share Button - Opens share modal */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 rounded-full hover:bg-green-500/10 hover:text-green-500 transition-all duration-200 h-9 px-3"
              onClick={(e) => {
                e.stopPropagation();
                setShowShareModal(true);
              }}
              data-testid={`share-btn-${post.id}`}
            >
              <Share2 className="w-[18px] h-[18px]" />
            </Button>

            {/* Gift Button - send a premium gift to the post author */}
            {post.user_id !== user?.id && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 rounded-full hover:bg-yellow-500/10 hover:text-yellow-500 transition-all duration-200 h-9 px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGiftModal(true);
                }}
                data-testid={`gift-btn-${post.id}`}
                title="Send a gift"
              >
                <Gift className="w-[18px] h-[18px]" />
              </Button>
            )}
            
            {/* Views Counter - Analytics */}
            <div className="flex items-center gap-1 text-muted-foreground ml-auto" data-testid={`views-${post.id}`}>
              <Eye className="w-4 h-4" />
              <span className="text-sm">{formatNumber(localViewsCount)}</span>
            </div>
          </div>
          
          {/* Mini Analytics Bar (visible on own posts) */}
          {isOwner && (localViewsCount > 0 || post.likes_count > 0) && (
            <div className="flex items-center gap-4 pt-2 mt-2 border-t border-border/50 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" />
                <span>Analistik:</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {formatNumber(localViewsCount)} views
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  {formatNumber(post.likes_count || 0)} likes
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" />
                  {formatNumber(post.comments_count || 0)} kòmantè
                </span>
                {post.likes_count > 0 && (
                  <span className="text-green-500">
                    {((post.likes_count / Math.max(localViewsCount || 1, 1)) * 100).toFixed(1)}% engagement
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Video Modal */}
      {isFullscreen && post.media_type === 'video' && (
        <div 
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          onClick={closeFullscreen}
          data-testid={`fullscreen-modal-${post.id}`}
        >
          {/* Close Button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
            data-testid={`close-fullscreen-btn-${post.id}`}
          >
            <X className="w-6 h-6" />
          </button>

          {/* User Info */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
            <Avatar className="w-10 h-10 ring-2 ring-white/30">
              <AvatarImage src={post.avatar} alt={post.display_name} />
              <AvatarFallback className="bg-gradient-to-br from-pink-500/20 to-violet-500/20 text-white font-semibold">
                {getInitials(post.display_name || post.username)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-semibold text-sm">{post.display_name || post.username}</p>
              <p className="text-white/60 text-xs">@{post.username}</p>
            </div>
          </div>

          {/* Video */}
          <video
            ref={fullscreenVideoRef}
            src={post.media[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${post.media[0]}` : post.media[0]}
            className="w-full h-full object-contain"
            muted={isMuted}
            loop
            playsInline
            autoPlay
            onClick={(e) => e.stopPropagation()}
            data-testid={`fullscreen-video-${post.id}`}
          />

          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            {/* Post Content */}
            {post.content && (
              <p className="text-white text-sm mb-4 max-w-2xl line-clamp-2">{post.content}</p>
            )}
            
            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Like */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleLike(e); }}
                  className="flex items-center gap-2 text-white"
                >
                  <Heart className={cn("w-6 h-6", post.is_liked && "fill-pink-500 text-pink-500")} />
                  <span className="text-sm">{formatNumber(post.likes_count)}</span>
                </button>
                
                {/* Comment */}
                <button
                  onClick={(e) => { e.stopPropagation(); closeFullscreen(); goToComments(e); }}
                  className="flex items-center gap-2 text-white"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span className="text-sm">{formatNumber(post.comments_count)}</span>
                </button>
                
                {/* Share */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleShare(); }}
                  className="flex items-center gap-2 text-white"
                >
                  <Share2 className="w-6 h-6" />
                </button>
              </div>

              {/* Right Controls */}
              <div className="flex items-center gap-3">
                {/* Mute/Unmute */}
                <button
                  onClick={toggleMute}
                  className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                
                {/* Exit Fullscreen */}
                <button
                  onClick={closeFullscreen}
                  className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
                >
                  <Minimize className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share to User Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Pataje ak yon zanmi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Chèche itilizatè..."
                value={shareSearchQuery}
                onChange={(e) => {
                  setShareSearchQuery(e.target.value);
                  searchUsersForShare(e.target.value);
                }}
                className="pl-10"
                data-testid="share-search-input"
              />
            </div>
            
            {/* Search Results */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {shareSearchResults.length === 0 && shareSearchQuery.length >= 2 && (
                <p className="text-center text-muted-foreground py-4">Pa gen rezilta</p>
              )}
              {shareSearchResults.map((targetUser) => (
                <div
                  key={targetUser.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={targetUser.avatar} />
                      <AvatarFallback>{getInitials(targetUser.display_name || targetUser.username)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{targetUser.display_name || targetUser.username}</p>
                      <p className="text-sm text-muted-foreground">@{targetUser.username}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleShareToUser(targetUser)}
                    disabled={sendingShare}
                    className="rounded-full"
                    data-testid={`share-to-${targetUser.id}`}
                  >
                    {sendingShare ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-1" />
                        Voye
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
            
            {/* Share externally option */}
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  handleShare();
                  setShowShareModal(false);
                }}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Pataje deyò app la
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Gift Modal (Coin Shop premium gifts) */}
      <SendGiftModal
        open={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        recipient={{
          id: post.user_id,
          username: post.username,
          display_name: post.display_name,
          avatar: post.avatar,
        }}
        targetType={post.media_type === 'video' ? 'video' : 'post'}
        targetId={post.id}
      />
    </article>
  );
};

export default PostCard;
